import { html, type TemplateResult } from "lit";
import { i18n } from "../i18n";

export type SortDirection = "asc" | "desc" | "unsorted";

export interface TableSort {
  key: string;
  direction: SortDirection;
}

export function nextTableSort(current: TableSort, key: string): TableSort {
  return {
    key,
    direction:
      current.key !== key || current.direction === "unsorted"
        ? "asc"
        : current.direction === "asc"
          ? "desc"
          : "unsorted",
  };
}

/** Omit sort parameters for the server's natural/default ordering. */
export function tableSortQuery(sort: TableSort): Record<string, string> {
  return sort.direction === "unsorted"
    ? {}
    : { sort: sort.key, direction: sort.direction };
}

function normalized(value: unknown): string | number {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return value ? 1 : 0;
  const text = String(value);
  const timestamp = /^\d{4}-\d{2}-\d{2}T/.test(text) ? Date.parse(text) : NaN;
  return Number.isNaN(timestamp) ? text.toLocaleLowerCase() : timestamp;
}

export function sortTableItems<T>(
  items: readonly T[],
  sort: TableSort,
  value: (item: T, key: string) => unknown,
): T[] {
  if (sort.direction === "unsorted") return [...items];
  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const a = normalized(value(left.item, sort.key));
      const b = normalized(value(right.item, sort.key));
      const result =
        typeof a === "number" && typeof b === "number"
          ? a - b
          : String(a).localeCompare(String(b), i18n.language, {
              numeric: true,
              sensitivity: "base",
            });
      return (
        (sort.direction === "asc" ? result : -result) ||
        left.index - right.index
      );
    })
    .map(({ item }) => item);
}

export function sortableTableHeader(
  label: string,
  key: string,
  sort: TableSort,
  onSort: (key: string) => void,
): TemplateResult {
  const active = sort.key === key && sort.direction !== "unsorted";
  const next = nextTableSort(sort, key).direction;
  return html`<th
    data-column-key=${key}
    aria-sort=${active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
  >
    <button
      class="table-sort-button"
      type="button"
      aria-label=${i18n.t(next === "asc" ? "sortAscending" : next === "desc" ? "sortDescending" : "sortUnsorted", { column: label })}
      @click=${() => onSort(key)}
    >
      <span>${label}</span
      ><span class="table-sort-indicator" aria-hidden="true"
        >${active ? (sort.direction === "asc" ? "▲" : "▼") : "↕"}</span
      >
    </button>
  </th>`;
}
